import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Building2,
  Wrench,
  HardHat,
  Users,
  ArrowRight,
  Mail,
  ShieldCheck,
  Sparkles,
  Loader2,
  LayoutGrid,
  FileText,
  Lock,
  CheckCircle2,
  ClipboardList,
  Settings2,
  Rocket,
  KeyRound,
  ScrollText,
  MapPin,
  HeartPulse,
  Factory,
  Briefcase,
  Hotel,
  Landmark,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { RequestDemoModal } from '../components/RequestDemoModal';

/**
 * Landing page publique (US-DASH-02).
 *
 *  - Hero orienté bénéfice + CTA primaire « Demander une démo ».
 *  - Bandeau secteurs, problèmes résolus, différenciateur Form Builder.
 *  - Modules métier, « comment ça marche », sécurité & conformité.
 *  - Compteurs animés (chiffres de démonstration — seront alimentés par
 *    des stats globales en Phase 12), FAQ, CTA final.
 *  - Responsive mobile-first (breakpoints Tailwind).
 *  - Si déjà authentifié → redirige vers `/dashboard`.
 */

export function LandingPage() {
  const { user, loading } = useAuth();
  // CTA d'origine du formulaire de démo (null = modal fermée).
  const [demoSource, setDemoSource] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white">
      {/* Header public */}
      <header className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center text-white font-bold">
              M
            </span>
            <span className="font-semibold text-gray-900">mygmao.pro</span>
          </Link>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setDemoSource('header')}
              className="hidden sm:inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors"
            >
              <Mail className="w-4 h-4 mr-2" />
              Demander une démo
            </button>
            <Link
              to="/login"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Se connecter
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero — split texte / aperçu produit */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10 sm:pt-24 sm:pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Colonne texte */}
          <div className="text-center lg:text-left">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 mb-6">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              Plateforme GMAO multi-secteur
            </span>
            <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 leading-tight tracking-tight">
              La GMAO qui <span className="text-primary-600">épouse votre métier</span>
            </h1>
            <p className="mt-4 sm:mt-6 max-w-xl mx-auto lg:mx-0 text-base sm:text-lg text-gray-600">
              Pilotez bâtiments, équipements, interventions et personnels depuis une seule
              plateforme. Adaptez chaque champ à votre secteur —{' '}
              <span className="font-semibold text-gray-800">sans une ligne de code</span>.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-3">
              <button
                type="button"
                onClick={() => setDemoSource('hero')}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors w-full sm:w-auto justify-center"
              >
                <Mail className="w-4 h-4 mr-2" />
                Demander une démo
              </button>
              <Link
                to="/login"
                className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto justify-center"
              >
                Se connecter
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap items-center lg:justify-start justify-center gap-x-5 gap-y-2 text-xs text-gray-500">
              <span className="inline-flex items-center">
                <CheckCircle2 className="w-4 h-4 mr-1.5 text-green-500" />
                Mise en route accompagnée
              </span>
              <span className="inline-flex items-center">
                <CheckCircle2 className="w-4 h-4 mr-1.5 text-green-500" />
                Données 100 % isolées
              </span>
              <span className="inline-flex items-center">
                <CheckCircle2 className="w-4 h-4 mr-1.5 text-green-500" />
                Hébergé en France
              </span>
            </div>
          </div>

          {/* Colonne aperçu produit */}
          <div className="relative">
            <div
              aria-hidden="true"
              className="absolute -inset-4 bg-gradient-to-tr from-primary-100 to-secondary-100 rounded-3xl blur-2xl opacity-60"
            />
            <div className="relative rounded-2xl bg-white border border-gray-200 shadow-xl overflow-hidden">
              {/* Barre de fenêtre */}
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-gray-100 bg-gray-50">
                <span className="w-3 h-3 rounded-full bg-red-300" />
                <span className="w-3 h-3 rounded-full bg-yellow-300" />
                <span className="w-3 h-3 rounded-full bg-green-300" />
                <span className="ml-3 text-xs text-gray-400">app.mygmao.pro/equipements</span>
              </div>
              {/* Contenu factice */}
              <div className="p-5">
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="rounded-lg bg-primary-50 p-3">
                    <p className="text-xs text-gray-500">Équipements</p>
                    <p className="text-xl font-bold text-gray-900">248</p>
                  </div>
                  <div className="rounded-lg bg-secondary-50 p-3">
                    <p className="text-xs text-gray-500">En panne</p>
                    <p className="text-xl font-bold text-gray-900">6</p>
                  </div>
                  <div className="rounded-lg bg-accent-50 p-3">
                    <p className="text-xs text-gray-500">Préventif</p>
                    <p className="text-xl font-bold text-gray-900">31</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <PreviewRow name="Compresseur A-12" status="En service" tone="green" />
                  <PreviewRow name="Groupe froid B-04" status="Maintenance" tone="yellow" />
                  <PreviewRow name="Ascenseur Nord" status="En panne" tone="red" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bandeau secteurs */}
      <section className="max-w-5xl mx-auto px-6 pb-12 sm:pb-16">
        <p className="text-center text-xs font-medium text-gray-400 uppercase tracking-wide mb-5">
          Conçu pour
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <SectorPill icon={HeartPulse} label="Santé" />
          <SectorPill icon={Factory} label="Industrie" />
          <SectorPill icon={Briefcase} label="Tertiaire" />
          <SectorPill icon={Hotel} label="Hôtellerie" />
          <SectorPill icon={Landmark} label="Collectivités" />
        </div>
      </section>

      {/* Problèmes résolus */}
      <section className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Fini les tableurs et les outils rigides
          </h2>
          <p className="mt-3 text-gray-600 max-w-xl mx-auto">
            Les problèmes du quotidien de la maintenance, résolus par conception.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <PainCard
            icon={LayoutGrid}
            problem="Un logiciel qui ne colle jamais à votre métier"
            answer="Form Builder visuel : ajoutez vos propres champs en quelques clics, sans développement."
          />
          <PainCard
            icon={ClipboardList}
            problem="Des interventions perdues entre mails et papier"
            answer="Cycle complet tracé : demande → validation → planification → exécution → clôture."
          />
          <PainCard
            icon={Lock}
            problem="Des données sensibles éparpillées"
            answer="Isolation stricte par établissement, journal d'audit automatique, RGPD-ready."
          />
        </div>
      </section>

      {/* Différenciateur Form Builder */}
      <section className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        <div className="rounded-2xl bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-100 p-8 sm:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white text-primary-700 mb-4">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Le différenciateur
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Votre métier a ses spécificités. Votre GMAO aussi.
              </h2>
              <p className="mt-4 text-gray-600 leading-relaxed">
                Numéro de lot pour l'industrie, classe de risque pour la santé, catégorie ERP pour
                le tertiaire… Avec le{' '}
                <span className="font-semibold text-gray-800">Form Builder</span>, votre
                administrateur ajoute, masque ou réorganise les champs de chaque entité —{' '}
                <span className="font-semibold text-gray-800">
                  sans développement, sans attendre une mise à jour
                </span>
                .
              </p>
            </div>
            <ul className="space-y-3">
              <FeatureLi text="16 types de champs (texte, nombre, date, liste, fichier…)" />
              <FeatureLi text="Schémas versionnés et auditables" />
              <FeatureLi text="Appliqué à toutes les entités majeures" />
              <FeatureLi text="Aucune compétence technique requise" />
            </ul>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Vos modules métier</h2>
          <p className="mt-3 text-gray-600 max-w-xl mx-auto">
            Adaptés à chaque secteur et configurables sans intervention de développement grâce au
            Form Builder.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <ModuleCard
            icon={Building2}
            title="Bâtiments"
            description="Patrimoine immobilier, étages, salles, réservations et documents techniques."
          />
          <ModuleCard
            icon={Wrench}
            title="Équipements"
            description="Inventaire, affectations, maintenance préventive et métrologie."
          />
          <ModuleCard
            icon={HardHat}
            title="Interventions"
            description="Cycle complet : demande, validation, planification, exécution, clôture."
          />
          <ModuleCard
            icon={Users}
            title="Personnels"
            description="Annuaire, rôles, services et permissions fines via RBAC."
          />
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Comment ça marche</h2>
          <p className="mt-3 text-gray-600 max-w-xl mx-auto">
            Opérationnel en quelques jours, pas en quelques mois.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <StepCard
            step={1}
            icon={Rocket}
            title="On crée votre espace"
            description="Un environnement isolé et sécurisé dédié à votre établissement."
          />
          <StepCard
            step={2}
            icon={Settings2}
            title="Vous configurez vos champs"
            description="Secteurs, modules et formulaires adaptés à votre métier via le Form Builder."
          />
          <StepCard
            step={3}
            icon={CheckCircle2}
            title="Vos équipes travaillent"
            description="Interventions, équipements et reporting, en temps réel et tracés."
          />
        </div>
      </section>

      {/* Sécurité & conformité */}
      <section className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Sécurité et conformité par défaut
          </h2>
          <p className="mt-3 text-gray-600 max-w-xl mx-auto">
            Vos données et celles de vos équipes protégées à chaque niveau.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <SecurityCard
            icon={ShieldCheck}
            title="Isolation multi-tenant"
            description="Cloisonnement strict par établissement, aucun accès croisé."
          />
          <SecurityCard
            icon={KeyRound}
            title="RBAC par rôle"
            description="Permissions fines par module et par action."
          />
          <SecurityCard
            icon={ScrollText}
            title="Journal d'audit"
            description="Toute action sensible automatiquement tracée."
          />
          <SecurityCard
            icon={MapPin}
            title="Hébergement souverain"
            description="Infrastructure auto-hébergée, données en France."
          />
          <SecurityCard
            icon={Lock}
            title="Mots de passe argon2id"
            description="Hachage à l'état de l'art, jamais en clair."
          />
          <SecurityCard
            icon={FileText}
            title="Fichiers protégés"
            description="Stockage chiffré et URLs signées à expiration courte."
          />
        </div>
      </section>

      {/* Compteurs animés */}
      <section className="max-w-6xl mx-auto px-6 py-8 sm:py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <AnimatedCounter
            label="Modules métier"
            target={4}
            suffix=""
            icon={Building2}
            variant="primary"
          />
          <AnimatedCounter
            label="Types de champs"
            target={16}
            suffix=""
            icon={Sparkles}
            variant="secondary"
          />
          <AnimatedCounter
            label="Secteurs couverts"
            target={5}
            suffix=""
            icon={ShieldCheck}
            variant="accent"
          />
          <AnimatedCounter
            label="Isolation des données"
            target={100}
            suffix="%"
            icon={Users}
            variant="primary"
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Questions fréquentes</h2>
        </div>
        <div className="space-y-3">
          <FaqItem
            question="Faut-il installer un logiciel ?"
            answer="Non. mygmao.pro est entièrement accessible depuis un navigateur web, sur ordinateur, tablette ou mobile."
          />
          <FaqItem
            question="Mes données sont-elles isolées des autres clients ?"
            answer="Oui. Chaque établissement dispose d'un espace strictement cloisonné (isolation par tenant). Aucun accès croisé n'est possible."
          />
          <FaqItem
            question="Puis-je adapter les champs à mon secteur ?"
            answer="Oui, via le Form Builder. Votre administrateur ajoute, masque ou réorganise les champs métier de chaque entité, sans développement."
          />
          <FaqItem
            question="Comment démarrer ?"
            answer="Demandez une démo : notre équipe crée votre espace, vous accompagne dans la configuration et la mise en route."
          />
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-4xl mx-auto px-6 py-12 sm:py-16">
        <div className="rounded-2xl bg-primary-500 text-white p-8 sm:p-12 text-center shadow-sm">
          <h2 className="text-2xl sm:text-3xl font-bold">
            Prêt à reprendre le contrôle de votre maintenance ?
          </h2>
          <p className="mt-3 text-primary-50 max-w-xl mx-auto">
            Contactez-nous pour créer l'espace de votre établissement. Notre équipe vous accompagne
            pour la configuration des champs métier et la mise en route.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => setDemoSource('cta_final')}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-primary-700 bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto justify-center"
            >
              <Mail className="w-4 h-4 mr-2" />
              Demander une démo
            </button>
            <Link
              to="/login"
              className="inline-flex items-center px-6 py-3 border border-white/40 rounded-lg text-sm font-medium text-white hover:bg-primary-600 transition-colors w-full sm:w-auto justify-center"
            >
              J'ai déjà un compte
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-8">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-2">
          <p>
            © {new Date().getFullYear()} mygmao.pro — Gestion de Maintenance Assistée par Ordinateur
          </p>
          <p>Multi-tenant · RBAC · Form Builder · Auto-audit · Sécurité ISO/RGPD ready</p>
        </div>
      </footer>

      {demoSource && <RequestDemoModal source={demoSource} onClose={() => setDemoSource(null)} />}
    </div>
  );
}

// ─── Helpers UI ─────────────────────────────────────────────────────────────

const variantClasses: Record<'primary' | 'secondary' | 'accent', string> = {
  primary: 'bg-primary-50 text-primary-600',
  secondary: 'bg-secondary-50 text-secondary-600',
  accent: 'bg-accent-50 text-accent-600',
};

function AnimatedCounter({
  label,
  target,
  suffix,
  icon: Icon,
  variant,
}: {
  label: string;
  target: number;
  suffix: string;
  icon: React.ComponentType<{ className?: string }>;
  variant: 'primary' | 'secondary' | 'accent';
}) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const duration = 900;
    const startTs = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const ratio = Math.min(1, (now - startTs) / duration);
      const eased = 1 - Math.pow(1 - ratio, 3);
      setValue(Math.round(eased * target));
      if (ratio < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-200 p-5">
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center ${variantClasses[variant]}`}
        >
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900">
        {value}
        <span className="text-gray-500 text-lg ml-0.5">{suffix}</span>
      </p>
    </div>
  );
}

function ModuleCard({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600 mb-4">
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
}

const previewToneClasses: Record<'green' | 'yellow' | 'red', string> = {
  green: 'bg-green-100 text-green-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  red: 'bg-red-100 text-red-700',
};

function PreviewRow({
  name,
  status,
  tone,
}: {
  name: string;
  status: string;
  tone: 'green' | 'yellow' | 'red';
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2">
      <span className="text-sm text-gray-700">{name}</span>
      <span className={`text-xs px-2 py-0.5 rounded-full ${previewToneClasses[tone]}`}>
        {status}
      </span>
    </div>
  );
}

function SectorPill({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 shadow-sm text-sm font-medium text-gray-700 hover:border-primary-200 hover:text-primary-700 transition-colors">
      <Icon className="w-4 h-4 text-primary-500" />
      {label}
    </span>
  );
}

function PainCard({
  icon: Icon,
  problem,
  answer,
}: {
  icon: React.ComponentType<{ className?: string }>;
  problem: string;
  answer: string;
}) {
  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-200 p-6">
      <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600 mb-4">
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-2">{problem}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{answer}</p>
    </div>
  );
}

function StepCard({
  step,
  icon: Icon,
  title,
  description,
}: {
  step: number;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="relative rounded-xl bg-white shadow-sm border border-gray-200 p-6">
      <span className="absolute top-6 right-6 text-4xl font-bold text-gray-100 select-none">
        {step}
      </span>
      <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600 mb-4">
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
}

function SecurityCard({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-200 p-5">
      <div className="flex items-center mb-2">
        <ShieldIcon Icon={Icon} />
        <h3 className="ml-3 text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
}

function ShieldIcon({ Icon }: { Icon: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center text-green-600 flex-shrink-0">
      <Icon className="w-5 h-5" />
    </div>
  );
}

function FeatureLi({ text }: { text: string }) {
  return (
    <li className="flex items-start">
      <CheckCircle2 className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-0.5" />
      <span className="text-sm text-gray-700">{text}</span>
    </li>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  return (
    <details className="group rounded-xl bg-white shadow-sm border border-gray-200 p-5 open:shadow-md transition-shadow">
      <summary className="flex items-center justify-between cursor-pointer list-none">
        <span className="text-sm font-semibold text-gray-900">{question}</span>
        <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90" />
      </summary>
      <p className="mt-3 text-sm text-gray-600 leading-relaxed">{answer}</p>
    </details>
  );
}
